import { useMemo } from "react";
import { useSelector } from "react-redux";
import type {
  OnboardingAnalyticsProps,
  OnboardingEntryPoint,
} from "@ifgengineering/client-analytics/src/types";
import { peekAnchoringDeal } from "@helpers/loginRedirect";
import { dealsSelector } from "@state/deal/deal.selectors";
import { OnboardingAnalyticsSurface, phaseOf } from "./onboardingAnalytics";
import { recallEntryPoint } from "./onboardingAnalyticsMemory";
import { dealIdFromPath } from "./routes";
import { useWebOnboardingV2B } from "./useWebOnboardingV2B";

export interface UseOnboardingAnalyticsContextArgs {
  surface: OnboardingAnalyticsSurface;
  /** Overrides the entry point remembered for this session, for a surface that knows its own. */
  entryPoint?: OnboardingEntryPoint;
}

/** The flow, version, phase, entry point and anchoring deal every onboarding event carries, resolved in one place. */
export const useOnboardingAnalyticsContext = ({
  surface,
  entryPoint,
}: UseOnboardingAnalyticsContextArgs): OnboardingAnalyticsProps => {
  const { isActive, isResolving } = useWebOnboardingV2B();
  const deals = useSelector(dealsSelector);
  const anchoringPath = peekAnchoringDeal();
  const anchoringDealId = anchoringPath
    ? dealIdFromPath(anchoringPath)
    : undefined;
  const anchoringDealName = anchoringDealId
    ? deals?.find(({ id }) => id === anchoringDealId)?.name
    : undefined;
  const resolvedEntryPoint = entryPoint ?? recallEntryPoint() ?? "resume";

  return useMemo<OnboardingAnalyticsProps>(
    () => ({
      ...(isResolving
        ? {}
        : {
            onboarding_flow: isActive ? "deal_anchored" : "standard",
            onboarding_version: isActive ? "deal_anchored" : "standard",
          }),
      onboarding_phase: phaseOf(surface),
      onboarding_entry_point: resolvedEntryPoint,
      ...(anchoringDealId ? { anchoring_deal_id: anchoringDealId } : {}),
      ...(anchoringDealName ? { anchoring_deal_name: anchoringDealName } : {}),
    }),
    [
      isActive,
      isResolving,
      surface,
      resolvedEntryPoint,
      anchoringDealId,
      anchoringDealName,
    ]
  );
};
